import React from "react";
import { useNavigate } from "react-router-dom";

// A small, display-only chip shown under a class name on the Schedule card,
// in Session Details and in the client Sessions tab. `variant="full"` shows
// the complete text (Session Details); the default compact variant is sized
// to fit inside a phone card at 390/360px and truncates when needed.
export function BillingChip({ billing, sessionId, variant = "compact" }) {
  const navigate = useNavigate();
  if (!billing) return null;
  const testid = `session-billing-${sessionId}`;
  const base =
    "inline-flex items-center gap-1 rounded-sm px-1 py-px text-[9px] font-bold uppercase tracking-wider self-start max-w-full truncate";

  // Rule 1 — its own invoice: the existing green INV chip, unchanged.
  if (billing.kind === "own") {
    return (
      <div
        data-testid={testid}
        data-billing="own"
        title={`Auto-generated ${billing.invoiceNumber} · ${String(billing.status || "").toUpperCase()}`}
        className={`${base} font-mono bg-[#0F6B6B]/15 border border-[#0F6B6B]/40 text-[#0F6B6B]`}
      >
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        <span className="truncate">{billing.invoiceNumber} · ${billing.total.toFixed(0)}</span>
      </div>
    );
  }

  // Rule 2 — covered by a pack credit: "PACK · INV-xxxx", tappable to open
  // the pack's sale invoice.
  if (billing.kind === "pack") {
    // A cancelled class whose credit was handed back shows no chip — the
    // "Pack credit returned." line carries the meaning instead.
    if (billing.packReturned) return null;
    const num = billing.packInvoiceNumber;
    const label = num ? `PACK · ${num}` : "PACK";
    const cls = `${base} font-mono bg-[#E1EDF7] border border-[#B9D4EC] text-[#1F5C8F]`;
    if (billing.packInvoiceId) {
      return (
        <button
          type="button"
          data-testid={testid}
          data-billing="pack"
          title={num ? `Covered by a pack credit — pack invoice ${num}` : "Covered by a pack credit"}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/invoices?focus=${billing.packInvoiceId}`);
          }}
          className={`${cls} hover:bg-[#D3E4F4] hover:border-[#9BC2E4] transition-colors cursor-pointer`}
        >
          <span className="truncate">{label}</span>
        </button>
      );
    }
    return (
      <div data-testid={testid} data-billing="pack" title="Covered by a pack credit" className={cls}>
        <span className="truncate">{label}</span>
      </div>
    );
  }

  const amber = `${base} bg-[#FBF4DD] border border-[#ECDCA6] text-[#7A5E18]`;

  // Rule 3 — completed with neither an invoice nor a credit.
  if (billing.kind === "none") {
    return (
      <div data-testid={testid} data-billing="none" title="Completed with no invoice and no pack credit" className={amber}>
        <span className="truncate">No invoice</span>
      </div>
    );
  }

  // Rule 4 — both a credit and an invoice.
  if (billing.kind === "check") {
    return (
      <div data-testid={testid} data-billing="check" title="Has both a pack credit and an invoice — check billing" className={amber}>
        <span className="truncate">Check billing</span>
      </div>
    );
  }

  return null;
}
